import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import debug from 'debug';

// Components
import { CheckBoxArrowTable } from '../../../../components/display/tables/CheckBoxArrowTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Styled Components
import { Container, ContainerButton } from '../../comunications/components/MainStyledComponents';

// Types
import { dataTypes } from '../../../../components/display/tables/CheckBoxArrowTable/types';
import { OrderType } from '../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Action and type modal
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../gql/queries/communication';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { selectCommunicationOrders } from '../../../../gql/selectors/communications';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';
import { withNavigationFeedback } from '../../../../utils/navigationFeedback';

const log = debug('rois:pages:communications:search');

if (import.meta.env.DEV) {
  debug.enable('rois:pages:communications:search*');
}

export default function HistoricalCommunicationsReportPage(): JSX.Element {
  const { t } = useTranslation();
  const data = useAsyncValue() as dataTypes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  // Persistent selection — all state managed by the hook
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data.ordersWithDataCommunication || [],
    totalCount: (data as any).totalCount || 0,
  });

  const openModalAction = useCallback(() => {
    log('Opening download modal with selectedRows:', selectedRows);
    log('Available data in table:', data);

    // Debug: Log the selected orders data
    const selectedOrdersData = data?.ordersWithDataCommunication?.filter(
      (order) => order.id && selectedRows.includes(order.id)
    );
    log('Selected orders data:', selectedOrdersData);

    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS)(store.getState());
    const { id: _id, take: _take, cursor: _cursor, ...filterFields } = currentCursor || {} as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: OrderType.Communication,
        // filterPositionColumn: Índice de la columna usada para filtrar las filas en el CSV
        // Estructura de columnas en CheckBoxArrowTable:
        //   0: Checkbox (ignorada con ignoreColumns: [0])
        //   1: Estat (SpecialTag con texto traducido, NO se debe usar para filtrar)
        //   2: ROID (valor único que coincide con selectedRows IDs)
        filterPositionColumn: 2,
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: (data as any).totalCount || 0,
      })
    );
  }, [selectedRows, data, dispatch, selectAllMode, excludedIds]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const communications = await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          roadId: currentCursor.roadId,
          concessionId: currentCursor.concessionId,
          interlocutorGroupId: currentCursor.interlocutorGroupId,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      roadId: currentCursor?.roadId,
      concessionId: currentCursor?.concessionId,
      interlocutorGroupId: currentCursor?.interlocutorGroupId,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      states: currentCursor?.states,
      userIds: currentCursor?.userIds,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      onlyFlagged: currentCursor?.onlyFlagged,
    };

    dispatch(
      setCursor(
        tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS,
        setCursorData
      )
    );

    await selectCommunicationOrders(
      communications.data.communications.communications,
      true
    );
    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          roadId: currentCursor.roadId,
          concessionId: currentCursor.concessionId,
          interlocutorGroupId: currentCursor.interlocutorGroupId,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  // 2026-08-04: antes navegaba a `summary/:id`, un detalle propio DENTRO de
  // Informes que además tenía su propio botón "Editar comunicación" — un tercer
  // camino de edición escondido en una sección de consulta. Ahora salta al
  // detalle del módulo, exactamente el mismo gesto que "Ver Recorrido" del
  // histórico del RV.
  const handleSeeCommunication = useCallback(() => {
    const tasks = () => withNavigationFeedback(async () => {
      if (selectedRows[0]) {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: selectedRows[0],
          },
        });

        navigate(`/work-orders/communications/${order.id}`);
      }
    });
    tasks().catch((err) => {
      Sentry.captureException(err);
      console.log(err);
    });
  }, [selectedRows, navigate]);

  return (
    <Container>
      <CheckBoxArrowTable
        idCss={'historical-' + OrderType.Communication}
        titleText={t('communications:filterBy')}
        data={data}
        setRowState={() => {}}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        rowState={selectedRows}
        tableId={tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS}
        getDoubleTapNavigationPath={(id) => `/work-orders/communications/${id}`}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectedCount={selectedCount}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
        showFlagColumn
        showOnlyFlaggedFilter
      />
      {/* "Editar comunicación" se fue a la tabla de Órdenes de Trabajo
          (2026-08-04). Informes filtra y descarga; para tocar algo se va al
          módulo, que es lo que ya hacían Actuaciones y Accidentes. */}
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('communications:view')}
          onClick={handleSeeCommunication}
          disabled={selectedCount !== 1}
        />
        <MainButton
          text={t('communications:downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={openModalAction}
        />
      </ContainerButton>
    </Container>
  );
}
